import React, { useMemo } from 'react'
import {
  Area,
  AreaChart as RechartsAreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { formatCurrency } from '@/lib/format'
import { CashFlowData } from '@/api/types'

interface CashFlowChartProps {
  data: CashFlowData | null
  branchId: number
  selectedYear: number
  onYearChange: (year: number) => void
  className?: string
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
]

/**
 * Cash Flow chart with year selector
 * Shows monthly cash balance as area chart with summary panel
 */
export const CashFlowChart: React.FC<CashFlowChartProps> = ({
  data,
  selectedYear,
  onYearChange,
  className,
}) => {
  // Generate years for dropdown (current year and 5 years back)
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear()
    return Array.from({ length: 6 }, (_, i) => currentYear - i)
  }, [])

  const handleYearChange = (value: string) => {
    const year = parseInt(value, 10)
    onYearChange(year)
  }

  // Prepare chart data - convert month numbers to month names
  const chartData = useMemo(() => {
    console.log('📊 CashFlowChart received data:', data)
    
    if (!data || !data.monthlyCashFlow || data.monthlyCashFlow.length === 0) {
      console.log('📊 No monthly cash flow data available')
      return []
    }

    const processed = data.monthlyCashFlow
      .sort((a, b) => a.month - b.month)
      .map((item) => {
        const cashBalance = typeof item.cashBalance === 'number' 
          ? item.cashBalance 
          : (Number(item.cashBalance) || 0)

        return {
          month: MONTHS[item.month - 1] || `Month ${item.month}`,
          monthNum: item.month,
          year: item.year,
          cashBalance: isFinite(cashBalance) && !isNaN(cashBalance) ? cashBalance : 0,
        }
      })
      .filter(item => {
        const isValid = item.monthNum >= 1 && item.monthNum <= 12 && 
                       !isNaN(item.cashBalance) && isFinite(item.cashBalance)
        
        if (!isValid) {
          console.warn('📊 Filtered out invalid month data:', item)
        }
        return isValid
      })

    console.log('📊 Processed cash flow chart data:', processed)
    return processed
  }, [data])

  // Calculate safe max value for YAxis domain
  const maxValue = useMemo(() => {
    console.log('📊 Calculating maxValue from chartData:', chartData)
    
    if (!chartData || chartData.length === 0) {
      console.log('📊 No chart data, returning default maxValue: 100')
      return 100
    }
    
    const allValues: number[] = []
    chartData.forEach((item, index) => {
      console.log(`📊 Item ${index}: cashBalance=${item.cashBalance}`)
      
      if (typeof item.cashBalance === 'number') {
        if (!isNaN(item.cashBalance) && isFinite(item.cashBalance)) {
          allValues.push(Math.abs(item.cashBalance))
          console.log(`📊 Added cashBalance: ${Math.abs(item.cashBalance)}`)
        } else {
          console.warn(`📊 Invalid cashBalance at index ${index}: ${item.cashBalance}`)
        }
      }
    })
    
    console.log('📊 All valid values:', allValues)
    
    if (allValues.length === 0) {
      console.log('📊 No valid values found, returning default maxValue: 100')
      return 100
    }
    
    const max = Math.max(...allValues)
    console.log('📊 Max raw value:', max)
    
    // 20% padding add karein with minimum 100
    const paddedMax = Math.max(100, Math.ceil(max * 1.2))
    console.log('📊 Padded max value:', paddedMax)
    
    // Final validation
    if (!isFinite(paddedMax) || isNaN(paddedMax) || paddedMax <= 0) {
      console.warn('📊 Invalid padded max, returning 1000')
      return 1000
    }
    
    console.log('📊 Final maxValue:', paddedMax)
    return paddedMax
  }, [chartData])

  // Calculate safe YAxis domain with fallback
  const safeDomain = useMemo(() => {
    const safeMax = (maxValue > 0 && isFinite(maxValue) && !isNaN(maxValue)) ? maxValue : 1000
    const domain: [number, number] = [0, safeMax]
    console.log('📊 Safe domain calculated:', domain, 'maxValue:', maxValue)
    return domain
  }, [maxValue])

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="rounded-lg border bg-background p-3 shadow-sm">
          <div className="space-y-2">
            <div className="font-semibold text-sm">{label}</div>
            <div className="space-y-1 text-xs">
              <div className="flex items-center justify-between gap-4">
                <span>Cash Balance:</span>
                <span className="font-medium">{formatCurrency(payload[0].value)}</span>
              </div>
            </div>
          </div>
        </div>
      )
    }
    return null
  }

  // Format Y-axis labels
  const formatYAxisLabel = (value: number) => {
    if (typeof value !== 'number' || !isFinite(value) || isNaN(value) || value < 0) {
      console.warn('📊 Invalid YAxis value in formatter:', value)
      return '0'
    }
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}k`
    }
    return value.toString()
  }

  // Calculate summary values
  const openingCash = data ? (typeof data.openingCash === 'number' ? data.openingCash : Number(data.openingCash) || 0) : 0
  const totalIncoming = data ? (typeof data.totalIncoming === 'number' ? data.totalIncoming : Number(data.totalIncoming) || 0) : 0
  const totalOutgoing = data ? (typeof data.totalOutgoing === 'number' ? data.totalOutgoing : Number(data.totalOutgoing) || 0) : 0
  const closingCash = data ? (typeof data.closingCash === 'number' ? data.closingCash : Number(data.closingCash) || 0) : 0

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Cash Flow</CardTitle>
            <CardDescription>
              Monthly cash balance overview
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            {/* Year Selector */}
            <Select value={String(selectedYear)} onValueChange={handleYearChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Select year" />
              </SelectTrigger>
              <SelectContent searchable={false}>
                {years.map((year) => (
                  <SelectItem key={year} value={String(year)}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!data || chartData.length === 0 || !isFinite(maxValue) || isNaN(maxValue) || maxValue <= 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No data available
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Chart - Takes 2 columns */}
            <div className="lg:col-span-2">
              {(() => {
                try {
                  return (
                    <ResponsiveContainer width="100%" height={350}>
                      <RechartsAreaChart
                        data={chartData}
                        margin={{
                          top: 5,
                          right: 30,
                          left: 20,
                          bottom: 5,
                        }}
                      >
                        <defs>
                          <linearGradient id="cashFlowGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#0f172a" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#0f172a" stopOpacity={0.1} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis
                          dataKey="month"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12 }}
                          className="text-muted-foreground"
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12 }}
                          className="text-muted-foreground"
                          tickFormatter={formatYAxisLabel}
                          domain={safeDomain}
                          allowDecimals={true}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area
                          type="monotone"
                          dataKey="cashBalance"
                          stroke="#0f172a"
                          fillOpacity={1}
                          fill="url(#cashFlowGradient)"
                          strokeWidth={2}
                        />
                      </RechartsAreaChart>
                    </ResponsiveContainer>
                  )
                } catch (error) {
                  console.error('📊 CashFlowChart rendering error:', error)
                  return (
                    <div className="text-center py-8 text-red-500">
                      Chart rendering failed. Please check the data.
                    </div>
                  )
                }
              })()}
            </div>

            {/* Summary Panel - Takes 1 column */}
            <div className="space-y-4">
              <div className="space-y-3">
                <div>
                  <div className="text-sm text-muted-foreground mb-1">
                    Cash as on 01 {MONTHS[0]} {selectedYear}
                  </div>
                  <div className="text-lg font-bold">
                    {formatCurrency(openingCash)}
                  </div>
                </div>
                
                <div>
                  <div className="text-sm text-muted-foreground mb-1">
                    Incoming
                  </div>
                  <div className="text-lg font-bold" style={{ color: '#0f172a' }}>
                    {formatCurrency(totalIncoming)} +
                  </div>
                </div>
                
                <div>
                  <div className="text-sm text-muted-foreground mb-1">
                    Outgoing
                  </div>
                  <div className="text-lg font-bold" style={{ color: '#64748b' }}>
                    {formatCurrency(totalOutgoing)} -
                  </div>
                </div>
                
                <div className="pt-2 border-t">
                  <div className="text-sm text-muted-foreground mb-1">
                    Cash as on 31 {MONTHS[11]} {selectedYear}
                  </div>
                  <div className="text-lg font-bold" style={{ color: '#0f172a' }}>
                    {formatCurrency(closingCash)} =
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

